/* eslint-disable react-hooks/rules-of-hooks */
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  IconButton,
  Popper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import { SketchPicker } from 'react-color';
import { Controller, useFormContext } from 'react-hook-form';

export interface RHFColorPickerProps {
  name: string;
  label: string;
  disabled?: boolean;
  fallbackColor?: string;
  variant?: 'inline' | 'card';
}

export default function RHFColorPicker({
  name,
  label,
  disabled = false,
  fallbackColor,
  variant = 'inline',
}: RHFColorPickerProps) {
  // const { } = useColorPicker();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openPopper, setOpenPopper] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      return;
    }

    setAnchorEl(event.currentTarget);
    setOpenPopper((prev) => !prev);
  };

  const handleClose = () => {
    setOpenPopper(false);
  };

  const { control } = useFormContext();

  return (
    <Box data-testid="colorPicker-render-test">
      <ClickAwayListener onClickAway={handleClose}>
        <Box>
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              const colorValue = field.value || fallbackColor || '#000000';

              return (
                <>
                  {variant === 'card' ? (
                    <ButtonBase
                      component="button"
                      disabled={disabled}
                      type="button"
                      onClick={(event) => handleClick(event)}
                      sx={{
                        width: 1,
                        minHeight: 92,
                        overflow: 'hidden',
                        borderRadius: 1.25,
                        border: (theme) =>
                          `1px solid ${alpha(theme.palette.grey[500], openPopper ? 0.34 : 0.18)}`,
                        bgcolor: 'background.paper',
                        textAlign: 'left',
                        justifyContent: 'stretch',
                        transition: (theme) =>
                          theme.transitions.create(['border-color', 'box-shadow', 'transform'], {
                            duration: theme.transitions.duration.shortest,
                          }),
                        boxShadow: openPopper
                          ? (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`
                          : 'none',
                        '&:hover': {
                          borderColor: (theme) => alpha(theme.palette.primary.main, 0.42),
                          boxShadow: (theme) =>
                            `0 10px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
                          transform: 'translateY(-1px)',
                        },
                        '&.Mui-disabled': {
                          opacity: 0.62,
                        },
                      }}
                    >
                      <Box sx={{ width: 8, alignSelf: 'stretch', bgcolor: colorValue }} />

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.5}
                        sx={{ width: 1, minWidth: 0, p: 1.5 }}
                      >
                        <Box
                          sx={{
                            width: 58,
                            height: 58,
                            flexShrink: 0,
                            borderRadius: 1.25,
                            bgcolor: colorValue,
                            border: (theme) =>
                              `1px solid ${alpha(theme.palette.common.black, 0.12)}`,
                            boxShadow: (theme) =>
                              `inset 0 1px 0 ${alpha(
                                theme.palette.common.white,
                                0.38
                              )}, inset 0 -12px 22px ${alpha(theme.palette.common.black, 0.12)}`,
                            backgroundImage:
                              'linear-gradient(135deg, rgba(255,255,255,0.34), rgba(255,255,255,0) 48%)',
                          }}
                        />

                        <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={800} noWrap>
                            {label}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              width: 'fit-content',
                              maxWidth: 1,
                              px: 1,
                              py: 0.45,
                              borderRadius: 0.75,
                              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                              color: 'text.secondary',
                              fontFamily: 'monospace',
                              letterSpacing: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {colorValue}
                          </Typography>
                        </Stack>

                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: 1,
                            color: 'text.secondary',
                            bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
                          }}
                        >
                          <PaletteRoundedIcon fontSize="small" />
                        </Box>
                      </Stack>
                    </ButtonBase>
                  ) : (
                    <Stack spacing={1} direction="row" alignItems="center">
                      <IconButton
                        disabled={disabled}
                        size="large"
                        sx={{ bgcolor: colorValue, border: '1px solid #aaaaaa' }}
                        onClick={(event) => handleClick(event)}
                      />
                      <Typography variant="caption">{label}</Typography>
                    </Stack>
                  )}

                  <Popper
                    anchorEl={anchorEl}
                    title="Selecione a cor"
                    open={openPopper && !disabled}
                    placement="top"
                    sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
                  >
                    <SketchPicker
                      disableAlpha
                      color={colorValue}
                      onChange={(color) => {
                        field.onChange(color.hex);
                      }}
                    />
                  </Popper>
                </>
              );
            }}
          />
        </Box>
      </ClickAwayListener>
    </Box>
  );
}
